import { useMemo, useState } from 'react';

function formatTime(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}

export default function Notifications({
    notifications = [],
    friendRequests = [],
    friends = [],
    getFriendData,
    acceptFriendRequest,
    declineFriendRequest,
    markNotificationRead,
    markAllNotificationsRead,
    showToast,
}) {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loadingProfile, setLoadingProfile] = useState(false);

    const recent = useMemo(() => [...notifications]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 30), [notifications]);
    const unreadCount = notifications.filter(n => !n.read).length;

    const openRequest = async (notification) => {
        await markNotificationRead(notification.id);
        const request = friendRequests.find(r => r.id === notification.id)
            || friendRequests.find(r => r.fromUid === notification.fromUid && r.status === 'pending')
            || {
                id: notification.id,
                fromUid: notification.fromUid,
                fromUserId: notification.fromUserId,
                fromNickname: notification.fromNickname,
            };
        setSelected(request);
        setProfile(null);
        if (request.fromUid && getFriendData) {
            setLoadingProfile(true);
            getFriendData(request.fromUid)
                .then(data => setProfile(data))
                .catch(() => setProfile(null))
                .finally(() => setLoadingProfile(false));
        }
    };

    const handleNotification = async (notification) => {
        if (notification.type === 'friend_request') {
            openRequest(notification);
            return;
        }
        await markNotificationRead(notification.id);
        setSelected(null);
    };

    const handleAccept = async () => {
        await acceptFriendRequest(selected);
        showToast('친구 요청을 수락했어요');
        setSelected(null);
    };

    const handleDecline = async () => {
        await declineFriendRequest(selected);
        showToast('친구 요청을 거절했어요');
        setSelected(null);
    };

    const alreadyFriend = selected && friends.some(f => f.uid === selected.fromUid);
    const requestActive = selected && friendRequests.some(r => r.id === selected.id);

    return (
        <div className="notification-wrap">
            <button className="notification-btn" onClick={() => setOpen(v => !v)} aria-label="알림">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 00-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
                    <path d="M13.73 21a2 2 0 01-3.46 0" />
                </svg>
                {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>

            {open && (
                <div className="notification-panel">
                    <div className="notification-head">
                        <div>
                            <div className="notification-title">알림</div>
                            <div className="notification-sub">최근 30개</div>
                        </div>
                        <button className="notification-read-all" onClick={markAllNotificationsRead}>모두 읽음</button>
                    </div>

                    {selected ? (
                        <div className="notification-detail">
                            <button className="back-btn mini" onClick={() => setSelected(null)}>← 알림 목록</button>
                            <div className="request-profile">
                                <div className="friend-avatar">{selected.fromNickname?.slice(0, 1) || '?'}</div>
                                <div>
                                    <div className="request-name">{selected.fromNickname || '사용자'}</div>
                                    <div className="request-id">@{selected.fromUserId || 'unknown'}</div>
                                </div>
                            </div>
                            {loadingProfile ? (
                                <div className="empty-text">···</div>
                            ) : (
                                <div className="request-stats">
                                    <div><strong>{profile?.records?.length || 0}</strong><span>기록</span></div>
                                    <div><strong>{profile?.friends?.length || 0}</strong><span>친구</span></div>
                                    <div><strong>{profile?.nickname || selected.fromNickname || '-'}</strong><span>닉네임</span></div>
                                </div>
                            )}
                            <div className="confirm-actions">
                                {alreadyFriend ? (
                                    <button className="action-btn" disabled>이미 친구</button>
                                ) : requestActive ? (
                                    <>
                                        <button className="action-btn" onClick={handleDecline}>거절</button>
                                        <button className="action-btn" onClick={handleAccept}>수락</button>
                                    </>
                                ) : (
                                    <button className="action-btn" disabled>처리된 요청</button>
                                )}
                            </div>
                        </div>
                    ) : recent.length === 0 ? (
                        <div className="notification-empty">새 알림이 없어요</div>
                    ) : (
                        <div className="notification-list">
                            {recent.map(item => (
                                <button
                                    key={item.id}
                                    className={`notification-item ${item.read ? '' : 'unread'}`}
                                    onClick={() => handleNotification(item)}
                                >
                                    <span className="notification-dot" />
                                    <span>
                                        <strong>{item.title || '알림'}</strong>
                                        <em>{item.body || '확인할 내용이 있어요'}</em>
                                    </span>
                                    <small>{formatTime(item.createdAt)}</small>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
